import { Button } from '@lifeomic/chroma-react/components/Button';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Box } from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { LexicalEditor } from 'lexical';
import {
  useActiveProjectId,
  useActiveProjectCohortId,
  useFeatures,
  useMixpanel,
  useNotify,
  useService,
} from '../../../../../hooks';
import { useApiClient } from '../../../../../hooks/useRestAPIHooks';
import { ClinicalImpressionService } from '../../../../../services/ClinicalImpressionService';
import { maskedModeActions } from '../../../../../redux/modules/maskedMode';
import { SYSTEM_RESOURCE_VISIBILITY } from '../../../../../util/fhir';
import { upsertReviewBaseFingerprintExtension } from '../../../../../util/contentFingerprint';
import { Annotation } from '../../../../TextAnnotation/types';
import { SuggestionsDrawer } from '../../SuggestionsDrawer';
import { AttachmentArbiterProvider } from '../AttachmentArbiter';
import { ViewMode } from '../Attachments/InlineAttachment';
import { ImpressionEditor } from '../ImpressionEditor';
import { CombinedRenderingTrackingProvider } from '../RenderingTracking';
import { useImpressionEditorContext } from '../ImpressionEditor/Context';
import { ControlBar } from '../ImpressionEditor/ControlBar';
import { ImpressionItem } from '../ImpressionEditor/ImpressionItem';
import { AnnotationPlugin } from '../ImpressionEditor/MarkdownEditor/plugins/AnnotationPlugin';
import { usePresentationKeyboardShortcuts } from '../ImpressionEditor/useKeyboardShortcuts';
import { useTemplatesContext } from './Context';
import {
  GenerateFormBody,
  messages as generateMessages,
  useGenerateForm,
} from './GenerateModal';
import { ReportPresentation } from './ReportPresentation';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.darkGraphite[50],
  },
  body: {
    flex: 1,
    overflow: 'auto',
    position: 'relative',
    height: '100%',
    padding: theme.spacing(2),
    zIndex: 10,
  },
}));

const messages = defineMessages({
  signedOff: {
    id: 'reportEditor.signedOff',
    defaultMessage: 'Signed off',
  },
  signOffFailed: {
    id: 'reportEditor.signOffFailed',
    defaultMessage: 'Failed to sign off. Please try again.',
  },
  selectReport: {
    id: 'reportEditor.selectReport',
    defaultMessage: 'Select a report to edit',
  },
});

const ReportDrawer: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  impression: ImpressionItem;
  annotations: Annotation[];
  onAnnotationsChange: (annotations: Annotation[]) => void;
  editorRef: React.RefObject<LexicalEditor | null>;
}> = ({
  isOpen,
  onClose,
  impression,
  annotations,
  onAnnotationsChange,
  editorRef,
}) => {
  const { formatMessage } = useIntl();
  const { patient, refetchImpression, refresh, setIsEditing, setIsSuggesting } =
    useImpressionEditorContext();

  const handleSuccess = useCallback(() => {
    setIsEditing(true);
    if (refetchImpression) {
      refetchImpression(impression.id);
    } else {
      refresh();
    }
  }, [setIsEditing, refetchImpression, impression.id, refresh]);

  const form = useGenerateForm({
    templateId: impression.templateId,
    onDismiss: onClose,
  });

  return (
    <SuggestionsDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={formatMessage(generateMessages.regenerate)}
      impressionId={impression.id}
      patientId={patient.id}
      summary={impression.summary ?? ''}
      annotations={annotations}
      onAnnotationsChange={onAnnotationsChange}
      editorRef={editorRef}
      onSuccess={handleSuccess}
      onFailureMessage={formatMessage(generateMessages.generateFailure)}
      onSuggestingChange={setIsSuggesting}
      regenerateTab={
        <GenerateFormBody form={form} activeImpression={impression} />
      }
      regenerateAction={
        <Button
          key="generate"
          onClick={form.handleGenerate}
          disabled={form.isGenerating || form.isRequesting}
        >
          {formatMessage(generateMessages.regenerate)}
        </Button>
      }
      showSuggestionsTab={impression.status !== 'completed'}
      rationale={impression.suggestionRationale}
      hasPendingDiff={!!impression.reviewDiffDocument}
    />
  );
};

const svc = new ClinicalImpressionService();

export const ReportEditor = ({
  refresh,
  patient,
  onClose,
  onDelete,
  impression: impressionProp,
}: {
  patient: fhir.Patient & { id: string };
  impression: ImpressionItem;
  onCancel?: () => void;
  onClose?: () => void;
  onDelete?: () => void;
  refresh?: () => void;
}) => {
  const classes = useStyles({});
  const apiClient = useApiClient();
  const mixpanel = useMixpanel();
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const [impressionOverride, setImpressionOverride] = useState<
    ImpressionItem | undefined
  >();

  const impression = impressionOverride ?? impressionProp;
  const [isEditing, setIsEditing] = useState(
    () => !!impression.reviewDiffDocument,
  );
  const [viewMode, setViewMode] = useState<ViewMode>('expanded');
  const [isPresenting, setIsPresenting] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);

  useEffect(() => {
    setImpressionOverride(undefined);
    setAnnotations([]);
    setIsDrawerOpen(false);
    setIsEditing(!!impressionProp.reviewDiffDocument);
  }, [impressionProp.id, impressionProp.reviewDiffDocument]);
  const editorRef = useRef<LexicalEditor | null>(null);
  const hasFeature = useFeatures();
  const shouldUseDrawer = hasFeature('zoriEditing');
  const isDraft = impression.status !== 'completed';
  const { invocations } = useTemplatesContext();

  const projectId = useActiveProjectId();
  const cohort = useActiveProjectCohortId();
  const [listImpressions] = useService(svc.listImpressions);
  const refetchImpression = useCallback(
    async (impressionId: string) => {
      const result = await listImpressions({
        patientId: patient.id,
        cohort,
        projectId,
        _id: impressionId,
      });
      const updated = result.data.entry?.[0]
        ?.resource as fhir.ClinicalImpression;
      if (updated) {
        setImpressionOverride(new ImpressionItem(updated));
      }
    },
    [listImpressions, patient.id, cohort, projectId],
  );
  const isGenerating = useMemo(() => {
    return invocations.some(
      invocation =>
        invocation.status === 'processing' &&
        invocation.agentInput.template_id === impression.templateId,
    );
  }, [invocations, impression.templateId]);

  const handleSignOff = async (signOffType?: 'member' | 'clinic') => {
    if (impression.status === 'completed') {
      return; // already signed off
    }
    try {
      // Patch impression status to completed
      const { data: latestImpression } = await apiClient.request(
        `GET /v1/fhir/dstu3/ClinicalImpression/:id`,
        { id: impression.id },
      );
      const baseSummary =
        typeof latestImpression.summary === 'string'
          ? latestImpression.summary
          : '';
      const extension = await upsertReviewBaseFingerprintExtension(
        latestImpression.extension,
        baseSummary,
      );
      const patched: typeof latestImpression = {
        ...latestImpression,
        status: 'completed',
        extension,
      };
      if (signOffType === 'clinic') {
        patched.meta = patched.meta ?? { tag: [] };
        patched.meta.tag = [
          ...(patched.meta.tag ?? []),
          {
            system: SYSTEM_RESOURCE_VISIBILITY,
            code: 'clinic-only',
          },
        ];
      }

      await apiClient.request(
        `PUT /v1/fhir/dstu3/ClinicalImpression/:resourceId`,
        { resourceId: impression.id, ...patched },
      );
      impression.impression.status = 'completed';
      refresh?.();
      setIsEditing(false);
      notify(formatMessage(messages.signedOff), 'success', 5000);
      mixpanel.track('zori_report_signed_off', {
        impressionType: impression.impressionType,
        impressionId: impression.id,
        visibility: signOffType === 'clinic' ? 'clinic_only' : 'member_visible',
      });
    } catch (err) {
      mixpanel.track('zori_report_sign_off_failed', {
        impressionType: impression.impressionType,
        impressionId: impression.id,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      notify(formatMessage(messages.signOffFailed), 'error', 5000);
    }
  };

  const handleFileUpload = () => {
    mixpanel.track(`zori_report_file_uploaded`, {
      impressionType: impression.impressionType,
      impressionId: impression.id,
    });
  };

  const handleSave = () =>
    mixpanel.track('zori_report_edited', {
      impressionType: impression.impressionType,
      impressionId: impression.id,
    });

  const handleSaveError = (err: unknown) =>
    mixpanel.track('zori_report_edit_failed', {
      impressionType: impression.impressionType,
      impressionId: impression.id,
      error: err instanceof Error ? err.message : 'Unknown error',
    });

  const reduxDispatch = useDispatch();
  usePresentationKeyboardShortcuts(
    useCallback(
      (newMode: ViewMode) => {
        setViewMode(newMode);
        reduxDispatch(maskedModeActions.setMaskedMode(false));
        setIsPresenting(true);
      },
      [reduxDispatch, setViewMode],
    ),
  );

  // Include viewMode so the tracker resets when the user opens presentation
  // mode — otherwise keys claimed by the main editor remain in the seen set
  // and the presentation view's widgets would all be suppressed.
  const renderContext = `${impression.id}|${isEditing}|${viewMode}`;

  return (
    <CombinedRenderingTrackingProvider context={renderContext}>
      <AttachmentArbiterProvider
        patient={patient}
        attachments={impression.attachments
          .filter((att): att is { reference: string } => !!att.reference)
          .map(att => att.reference)}
      >
        <ReportPresentation
          isOpen={isPresenting}
          onDismiss={() => {
            setIsPresenting(false);
            setViewMode('expanded');
          }}
          patient={patient}
          impression={impression}
          viewMode={viewMode}
        >
          <Box className={classes.body}>
            {isPresenting && (
              <ImpressionEditor.Component
                viewMode={viewMode}
                patient={patient}
                impression={impression}
                hideTitle
              />
            )}
          </Box>
        </ReportPresentation>
      </AttachmentArbiterProvider>
      <ImpressionEditor.Providers
        patient={patient}
        impression={impression}
        viewMode={viewMode}
        refresh={refresh}
        refetchImpression={refetchImpression}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        canAnnotate={
          shouldUseDrawer && isDraft && !impression.reviewDiffDocument
        }
      >
        <Box className={classes.root}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              backgroundColor: 'background.paper',
            }}
          >
            <ControlBar
              onClose={onClose}
              onSignOff={handleSignOff}
              viewMode={viewMode}
              onSelectViewMode={newMode => {
                setViewMode(newMode);
                if (
                  newMode === 'presentation-expanded' ||
                  newMode === 'presentation-compact'
                ) {
                  setIsPresenting(true);
                }
              }}
              isGenerating={isGenerating}
              onTriggerRegeneration={
                shouldUseDrawer
                  ? () => setIsDrawerOpen(prev => !prev)
                  : undefined
              }
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            <Box className={classes.body} sx={{ flex: 1, minWidth: 0 }}>
              {!isPresenting && (
                <ImpressionEditor.Render
                  onDelete={onDelete}
                  onFileUpload={handleFileUpload}
                  onSave={handleSave}
                  onSaveError={handleSaveError}
                />
              )}
            </Box>
            {shouldUseDrawer && isDraft && !impression.reviewDiffDocument && (
              <AnnotationPlugin
                annotations={annotations}
                onAnnotationsChange={setAnnotations}
                onRequestDrawerOpen={() => setIsDrawerOpen(true)}
                isEditing={isEditing}
                canAnnotate
                editorRef={editorRef}
              />
            )}
            {shouldUseDrawer && (
              <ReportDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                impression={impression}
                annotations={annotations}
                onAnnotationsChange={setAnnotations}
                editorRef={editorRef}
              />
            )}
          </Box>
        </Box>
      </ImpressionEditor.Providers>
    </CombinedRenderingTrackingProvider>
  );
};
